import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SupportedLangCode } from '@/application/i18n/language'

export type ReadAloudEngineChoice = 'site' | 'device'

interface ReadAloudState {
  /** Playback speed multiplier, shared across languages/articles. */
  rate: number
  /** The visitor's chosen `TtsVoice.id` per language — only ever set for the
   * article's primary (title) language, since that's the only language the
   * voice menu offers a choice for. Absent means "Automatic". */
  voiceIdByLang: Partial<Record<SupportedLangCode, string>>
  /** Which voices to speak with: the site's own downloadable voices
   * (`'site'`, the default) or the operating system's (`'device'`). */
  engine: ReadAloudEngineChoice
  /** Whether the visitor agreed to download the site voice models. Never
   * downloaded without it. */
  modelConsent: boolean
  setRate: (rate: number) => void
  setEngine: (engine: ReadAloudEngineChoice) => void
  setModelConsent: (consent: boolean) => void
  /** `voiceId: null` clears the preference (back to "Automatic"). */
  setVoice: (lang: SupportedLangCode, voiceId: string | null) => void
}

/**
 * Persists the read-aloud widget's user *preferences* only (speed, chosen
 * voice per language, engine choice, model-download consent) — never ephemeral playback state (that lives in
 * `useReadAloud`'s own component state), following the same
 * preferences-only persistence pattern as `consent.store.ts`.
 */
export const useReadAloudStore = create<ReadAloudState>()(
  persist(
    (setState) => ({
      rate: 1,
      voiceIdByLang: {},
      engine: 'site',
      modelConsent: false,

      setEngine: (engine) => setState({ engine }),

      setModelConsent: (modelConsent) => setState({ modelConsent }),

      setRate: (rate) => setState({ rate }),

      setVoice: (lang, voiceId) =>
        setState((current) => {
          const next = { ...current.voiceIdByLang }
          if (voiceId) {
            next[lang] = voiceId
          } else {
            delete next[lang]
          }
          return { voiceIdByLang: next }
        }),
    }),
    {
      name: 'read-aloud-preferences',
      partialize: (state) => ({
        rate: state.rate,
        voiceIdByLang: state.voiceIdByLang,
        engine: state.engine,
        modelConsent: state.modelConsent,
      }),
    },
  ),
)
